import { useQuery } from "@apollo/client"
import { Box, CircularProgress, Typography } from "@mui/material"
import { ReportLeftBox } from "components/ReportLeftBox/ReportLeftBox"
import { ReportMiddleBox } from "components/ReportMiddleBox/ReportMiddleBox"
import { ReportRightBox } from "components/ReportRightBox/ReportRightBox"
import { GET_PROJECTS, GET_REPORT_BY_ID } from "graphql/queries"
import { Project, Report } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useCustomerByBusinessAccount } from "hooks/useCustomerData"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router"

export const ReportPage = () => {
  const { projectID, reportID } = useParams()
  const projects = useStoreState((state) => state.projects)
  const leads = useStoreState((state) => state.leads)
  const { updateReport, fetchAllReportsBasicData } = useStoreActions((action) => action)
  const fetchLeads = useStoreActions((state) => state.fetchLeads)
  const setProjects = useStoreActions((state) => state.setProjects)
  const [project, setProject] = useState<Project | null>(null)
  const [unsavedReport, setUnsavedReport] = useState<Report | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const { reports } = useStoreState((state) => state)

  // Query the new separate History collection
  const {
    data: reportData,
    loading: reportLoading,
    error: reportError,
    // refetch: refetchReport,
  } = useQuery(GET_REPORT_BY_ID, {
    variables: {
      id: reportID,
    },
    skip: !reportID,
    errorPolicy: "all", // Continue even if there are GraphQL errors
    fetchPolicy: "cache-and-network", // Show cache, always refresh from network
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  })
  // Surface errors via snackbar
  useEffect(() => {
    if (reportError) {
      enqueueSnackbar("Fehler beim Laden des Reports: " + reportError.message, {
        variant: "error",
      })
    }
  }, [reportError])

  // Fetch projects if not in store (happens on direct page navigation)
  // Only fetch if we don't have the specific project we need
  const projectExists = projects.some((p) => p?.id === projectID)
  const { data: projectsData } = useQuery(GET_PROJECTS, {
    skip: projects.length > 0 || projectExists, // Skip if projects already loaded or specific project exists
    fetchPolicy: "cache-first", // Use cache when available
  })

  // Update projects store if fetched
  useEffect(() => {
    if (projectsData?.getProjects) {
      setProjects(projectsData.getProjects)
    }
  }, [projectsData, setProjects])

  useEffect(() => {
    const ensureReports = async () => {
      try {
        await fetchAllReportsBasicData({ fresh: true })
      } catch (err) {
        console.error("ReportPage: Fehler beim Laden der Reports:", err)
        enqueueSnackbar("Fehler beim Laden der Reports. Bitte erneut versuchen.", {
          variant: "error",
        })
      }
    }

    ensureReports()
  }, [fetchAllReportsBasicData])

  useEffect(() => {
    const project = projects.find((project) => project && project?.id === projectID)
    if (project) {
      setProject(project)
    }
  }, [projectID, projects])

  // Ensure leads are available to derive businessAccount for customer lookup
  useEffect(() => {
    if (project && (!leads || leads.length === 0)) {
      fetchLeads({ fresh: false }).catch((err: any) => {
        console.error("ReportPage: Fehler beim Laden der Leads:", err)
      })
    }
  }, [project, leads, fetchLeads])

  useEffect(() => {
    if (reportData && reportData.getReportById) {
      setUnsavedReport(reportData.getReportById)
      setHasUnsavedChanges(false)
    }
  }, [reportData, reportError, reportID, reportLoading])

  // Derive businessAccount from project.lead or from leads store using opportunityID
  const businessAccount = (() => {
    if (project?.lead?.businessAccount) return project.lead.businessAccount as string
    const oppId = project?.lead?.opportunityID || project?.lead?.id
    if (!oppId) return undefined
    const lead = leads.find((l) => l?.opportunityID === oppId)
    return lead?.businessAccount || undefined
  })()

  // Fetch customer live based on businessAccount
  const { customer } = useCustomerByBusinessAccount(businessAccount)

  // Attach project and customer to the working report for in-editor rendering (not persisted)
  useEffect(() => {
    if (unsavedReport && project) {
      setUnsavedReport((prev) => ({ ...(prev as any), project, customer }) as any)
    }
  }, [project, customer])

  const handleReportChange = (updatedReport: Report | Partial<Report>) => {
    if ("id" in updatedReport && "chapter" in updatedReport) {
      // Full Report object
      setUnsavedReport(updatedReport as Report)
    } else if (unsavedReport) {
      // Partial update - merge with existing report
      setUnsavedReport({ ...unsavedReport, ...updatedReport } as Report)
    }
    setHasUnsavedChanges(true)
  }

  const handleSaveReport = useCallback(async () => {
    if (projectID && unsavedReport) {
      await updateReport({ projectID: projectID, report: unsavedReport })
      setHasUnsavedChanges(false)
    }
  }, [projectID, unsavedReport, updateReport])

  // Add keyboard shortcut for saving
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault()
        if (hasUnsavedChanges) {
          handleSaveReport()
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [hasUnsavedChanges, handleSaveReport])

  const handleSaveAsNewVersion = async () => {
    const projectReports = reports.filter((report) => report.projectID === projectID)

    // Find the highest version number among existing reports
    let highestVersion = "1.0"

    if (projectReports.length > 0) {
      // Parse all version strings and find the highest
      const versions = projectReports.map((report) => {
        const version = report.version || "1.0"
        return { version }
      })

      // Find the highest version by comparing version strings
      const highestVersionData = versions.reduce((highest, current) => {
        // Convert version strings to numbers for comparison (e.g., "1.0" -> 1.0, "1.01" -> 1.01)
        const currentVersionNum = parseFloat(current.version) || 1.0
        const highestVersionNum = parseFloat(highest.version) || 1.0
        return currentVersionNum > highestVersionNum ? current : highest
      })

      highestVersion = highestVersionData.version
    }

    // Increment the version by 0.01
    const currentVersionNum = parseFloat(highestVersion) || 1.0
    const newVersionNum = currentVersionNum + 0.01
    const newVersion = newVersionNum.toFixed(2)

    if (projectID && unsavedReport) {
      const newVersionReport = {
        ...unsavedReport,
        version: newVersion,
        id: `${unsavedReport.id}_v${unsavedReport.version + 1}`, // This will be replaced by backend
      }
      await updateReport({ projectID: projectID, report: newVersionReport })
      setUnsavedReport(newVersionReport)
      setHasUnsavedChanges(false)
      try {
        await fetchAllReportsBasicData({ fresh: true })
      } catch (err) {
        console.error("ReportPage: Fehler beim Aktualisieren der Reportliste:", err)
        enqueueSnackbar("Fehler beim Aktualisieren der Reportliste.", {
          variant: "error",
        })
      }
    }
  }

  // Show loading state while data is being fetched
  if (reportLoading || (!project && projects.length === 0 && !projectsData)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 115px)",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  // Show error state
  if (reportError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 115px)",
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          Fehler beim Laden des Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {reportError.message}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Report ID: {reportID}
        </Typography>
      </Box>
    )
  }

  // Show message if no report found
  if (!reportLoading && !unsavedReport && reportData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 115px)",
          gap: 2,
        }}
      >
        <Typography variant="h6">Report nicht gefunden</Typography>
        <Typography variant="body2" color="text.secondary">
          Der angeforderte Report konnte nicht gefunden werden.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Report ID: {reportID}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {project !== null && unsavedReport !== null && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "calc(100vh - 115px)",
            overflow: "hidden",
          }}
        >
          <ReportLeftBox
            project={project}
            report={unsavedReport}
            handleReportChange={handleReportChange}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={handleSaveReport}
            onSaveAsNewVersion={handleSaveAsNewVersion}
          />

          <ReportMiddleBox report={unsavedReport} updateReport={handleReportChange} />

          <ReportRightBox project={project} />
        </Box>
      )}
    </>
  )
}
